import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import callRpc from '~publish/legacy/utils/call-rpc'

import type { AutocompleteItem } from '../../autocomplete'

export type FacebookMentionResponseItem = {
  id: string
  link: string
  name: string
}

export const failedFacebookAutocompleteRequestMessage = `Oops! We lost access to your Facebook account, and can't provide
Facebook page tag suggestions as a result. Would you be up for
refreshing that channel connection and trying again?`

const isFacebookMentionResponse = (
  items: unknown,
): items is FacebookMentionResponseItem[] => {
  if (!Array.isArray(items)) {
    return false
  }

  return items.every((item) => {
    return (
      typeof item === 'object' &&
      item !== null &&
      'id' in item &&
      'link' in item &&
      'name' in item
    )
  })
}

const isFacebookMentionResponseError = (error: unknown): boolean => {
  return (
    !!error &&
    typeof error === 'object' &&
    !Array.isArray(error) &&
    'code' in error
  )
}

export function parseSuggestions(
  suggestions: FacebookMentionResponseItem[],
): AutocompleteItem[] {
  return suggestions?.map((mention) => ({
    key: mention.id,
    text: mention.name,
    data: {
      id: mention.id,
      handle: `@${mention.id}`,
      name: mention.name,
      avatar: `https://graph.facebook.com/${mention.id}/picture?type=square`,
      url: mention.link,
    },
  }))
}

/**
 * Fetches new Facebook mention suggestions for the given search text.
 * Throws on failure (a fetch/RPC error, or an error-shaped response) — the
 * caller (useAutocompleteSuggestions) surfaces that as its own `error`
 * state for the render component to show.
 */
export const getNewSuggestionsOnMentionUpdate = async (
  editor: BufferEditor,
  searchText: string,
): Promise<AutocompleteItem[]> => {
  const editorComposerData = editor.getEditorComposerData?.(editor)
  const profileId = editorComposerData?.selectedProfilesForService[0]?.id

  if (!profileId || !searchText) return []

  const searchResults: unknown = await callRpc('composerApiProxy', {
    url: `/1/search/facebook_pages.json`,
    args: {
      q: searchText,
      profile_id: profileId,
    },
  })

  if (Array.isArray(searchResults) && searchResults.length === 0) return []

  if (isFacebookMentionResponse(searchResults)) {
    return parseSuggestions(searchResults)
  }

  throw isFacebookMentionResponseError(searchResults)
    ? searchResults
    : new Error('Unexpected Facebook mention search response')
}
